/**
 * El día y el turno: lo que «Empezar el día», «Terminar mi turno» y «Recibir la caja» deciden
 * solos.
 *
 * The till is per shift, and a shop can have several shifts a day, each counted at both ends (the
 * owner, 2026-10-03). Which name a start gets, and whether a count is asked again, are worked out
 * here, apart from the screens, so the rules can be tested.
 */

import { ZONA_HORARIA, esCamion, fechaEnNicaragua, formatMoney, type Centavos } from '@abastece/shared';
import { responsableDe, type SesionCaja } from '@abastece/core';
import type { Clave, Valores } from '../i18n/index.js';
import type { Moneda } from '../ui/ContarBilletes.js';
import { envolver, fila } from './recibo.js';

/**
 * The day a shift goes by: the day it opened, in Nicaragua (`historialDinero.fechaDelTurno`). A
 * night shift that closes at twenty past midnight is still the night before's: by the day it closed,
 * «Terminar el día» summed it apart from the morning's, and «Empezar el día» the next morning came
 * out «Empezar mi turno» (2026-10-13).
 */
export const diaDelTurno = (s: Pick<SesionCaja, 'abiertaEn'>): string => fechaEnNicaragua(s.abiertaEn);

/** Until four in the morning, the night before is still the shop's day: a till still open then is not yet left open overnight. */
const MADRUGADA_MS = 4 * 3_600_000;

/** The shop's day now: today's date, but the day before until four in the morning (`cajasDeOtroDia`). */
export function hoyDelNegocio(ahora: Date = new Date()): string {
  return fechaEnNicaragua(new Date(ahora.getTime() - MADRUGADA_MS).toISOString());
}

/**
 * Whether a start on this device, at `ubicacion`, is the day's first: no shift there that started
 * today has closed (`diaDelTurno`). The first is «Empezar el día», and says what the day holds; the
 * ones after it are «Empezar mi turno». Whether somebody follows at the end cannot be known like
 * this, and is asked.
 */
export function primeroDelDia(
  sesiones: readonly SesionCaja[],
  dispositivoId: string,
  ubicacion: string,
  hoy: string,
): boolean {
  return !sesiones.some(
    (s) =>
      s.dispositivoId === dispositivoId &&
      s.ubicacion === ubicacion &&
      s.estado === 'cerrada' &&
      s.cerradaEn !== null &&
      diaDelTurno(s) === hoy,
  );
}

/**
 * Whether a count is asked again before it is taken: off what was expected by `umbral` córdobas
 * or more (`cajaReconteoDesde`), or by any dollar, since dollars are counted in whole bills and a
 * bill off is never a coin's rounding. Nothing expected, nothing to hold the count against.
 */
export function pideReconteo(contado: Centavos, esperado: Centavos | null, moneda: Moneda, umbral: number): boolean {
  if (esperado === null) return false;
  const diferencia = Math.abs(contado - esperado);
  if (diferencia === 0) return false;
  return moneda === 'USD' || diferencia >= umbral;
}

/** A till still open from a day before today: whoever has it, and since which day. */
export interface CajaDeOtroDia {
  sesion: SesionCaja;
  /** Whoever opened it, or received it last. */
  usuarioId: string;
  /** The day in Managua they have had it since. */
  desde: string;
}

/**
 * The tills still open from a day before `hoy`, the oldest first, off the trucks: a route has its
 * own reminder, and closes with its settlement. A drawer is counted at its corte and at every
 * handover, so a till is as old as the day its holder has had it since: opened yesterday and
 * received this morning, this morning's count started it afresh. One that went a night without
 * either was never counted at the end of its day, and whatever is missing from it by the time it
 * is cannot be told apart from what went missing the day after (the owner asked, 2026-10-03, what
 * cures a step somebody forgot: this says so the morning after, while it can still be put right).
 *
 * The last handover as the books keep it (`entregadaEn`), not from the log: a pull marks every
 * event it brings confirmed, and a handover the books refused counted nothing.
 *
 * `hoy` is the shop's day (`hoyDelNegocio`): a night shift still at the till at five past midnight
 * has not been left open overnight.
 */
export function cajasDeOtroDia(sesiones: readonly SesionCaja[], hoy: string): CajaDeOtroDia[] {
  return sesiones
    .filter((s) => s.estado === 'abierta' && !esCamion(s.ubicacion))
    .map((s) => {
      const abierta = fechaEnNicaragua(s.abiertaEn);
      const entregada = s.entregadaEn ? fechaEnNicaragua(s.entregadaEn) : '';
      return { sesion: s, usuarioId: responsableDe(s), desde: entregada > abierta ? entregada : abierta };
    })
    .filter((x) => x.desde < hoy)
    .sort((a, b) => a.desde.localeCompare(b.desde));
}

// ---------------------------------------------------------------------------
// El resumen del día: lo que se imprime o se manda al dueño al terminar el día.
// ---------------------------------------------------------------------------

/** A shift of the day, closed, and who had it: whoever opened, and whoever closed if not them. */
export interface TurnoDelDia {
  sesion: SesionCaja;
  abrio: string;
  cerro: string | null;
}

export type Traducir = (clave: Clave, valores?: Valores) => string;

/** A receipt's width: a line of a 58 mm roll, and still a line in a chat bubble. */
export const ANCHO = 32;

/** A label and its amount on one line, the amount to the right; the label cut at the spaces when they do not fit. */
export { fila };

const dolares = (c: number) => `US$ ${formatMoney(c as Centavos, { symbol: false })}`;
/** A difference, with its sign: «+C$ 20.00», «-US$ 5.00». */
function firmado(c: number, enDolares = false): string {
  if (!enDolares) return formatMoney(c as Centavos, { signed: true });
  const signo = Math.sign(c) === 1 ? '+' : Math.sign(c) === -1 ? '-' : '';
  return signo + dolares(Math.abs(c));
}

/** What a count found against what was expected, in a few words. */
function veredicto(t: Traducir, diferencia: number, enDolares = false): string {
  if (diferencia === 0) return t('turno.r.cuadro');
  const monto = enDolares ? dolares(Math.abs(diferencia)) : formatMoney(Math.abs(diferencia) as Centavos);
  return t(diferencia > 0 ? 'turno.r.sobro' : 'turno.r.falto', { monto });
}

/**
 * The day at this drawer, shift by shift: what each sold and how it was paid, the cash that came
 * in and went out besides, what each count found, a recount's first count among them, what was
 * left for change and what was taken out; then the day's total. Thirty-two characters wide, like a
 * receipt, so the same text prints on the roll, goes to the owner by WhatsApp, and is what the
 * screen shows: what the owner reads is what was printed. In the language of whoever ends the day.
 */
export function textoDelDia(
  turnos: readonly TurnoDelDia[],
  negocio: string,
  fecha: string,
  t: Traducir,
  locale: string,
): string {
  const hora = (iso: string) =>
    new Date(iso).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', timeZone: ZONA_HORARIA });
  const dia = new Date(`${fecha}T12:00:00Z`).toLocaleDateString(locale, { dateStyle: 'medium', timeZone: 'UTC' });
  const lineas: string[] = [...envolver(negocio), ...envolver(t('turno.r.titulo', { fecha: dia })), '='.repeat(ANCHO)];

  let vendido = 0;
  let fiado = 0;
  let otros = 0;
  let diferencias = 0;
  let diferenciasDolares = 0;
  turnos.forEach(({ sesion: s, abrio, cerro }, i) => {
    if (i > 0) lineas.push('-'.repeat(ANCHO));
    lineas.push(...envolver(cerro && cerro !== abrio ? `${abrio} → ${cerro}` : abrio));
    lineas.push(`${hora(s.abiertaEn)} – ${s.cerradaEn ? hora(s.cerradaEn) : ''}`);

    const sFiado = s.fiado ?? 0;
    vendido += s.totalVendido;
    fiado += sFiado;
    otros += s.otrosPagos;
    lineas.push(fila(t('turno.r.vendido'), formatMoney(s.totalVendido)));
    if (s.totalVendido > 0) {
      lineas.push(fila(` ${t('turno.r.enEfectivo')}`, formatMoney((s.totalVendido - sFiado - s.otrosPagos) as Centavos)));
      if (s.otrosPagos > 0) lineas.push(fila(` ${t('turno.r.otros')}`, formatMoney(s.otrosPagos)));
      if (sFiado > 0) lineas.push(fila(` ${t('turno.r.fiado')}`, formatMoney(sFiado as Centavos)));
    }
    if (s.efectivoDolares > 0) lineas.push(fila(t('turno.r.dolares'), dolares(s.efectivoDolares)));
    if ((s.abonos ?? 0) > 0) lineas.push(fila(t('turno.r.abonos'), formatMoney(s.abonos!)));
    if ((s.devoluciones ?? 0) > 0) lineas.push(fila(t('turno.r.devoluciones'), formatMoney(-s.devoluciones! as Centavos)));
    if (s.retiros > 0) lineas.push(fila(t('turno.r.retiros'), formatMoney(-s.retiros as Centavos)));
    if ((s.retirosDolares ?? 0) > 0) lineas.push(fila(t('turno.r.retiros'), `-${dolares(s.retirosDolares!)}`));
    if ((s.pagado ?? 0) > 0) lineas.push(fila(t('turno.r.pagado'), formatMoney(-s.pagado! as Centavos)));

    // What the counts found: opening, handovers, the corte.
    if ((s.diferenciaApertura ?? 0) !== 0) {
      lineas.push(fila(t('turno.r.alAbrir'), firmado(s.diferenciaApertura!)));
      diferencias += s.diferenciaApertura!;
    }
    if (s.primerConteoApertura != null) lineas.push(fila(` ${t('turno.r.primerConteo')}`, formatMoney(s.primerConteoApertura)));
    if ((s.diferenciaEntregas ?? 0) !== 0) {
      lineas.push(fila(t('turno.r.entregas'), firmado(s.diferenciaEntregas!)));
      diferencias += s.diferenciaEntregas!;
    }
    if (s.primerConteoEntregaCordobas != null)
      lineas.push(fila(` ${t('turno.r.primerConteo')}`, formatMoney(s.primerConteoEntregaCordobas)));
    if (s.primerConteoEntregaDolares != null)
      lineas.push(fila(` ${t('turno.r.primerConteo')}`, dolares(s.primerConteoEntregaDolares)));
    const difC = s.diferenciaCordobas ?? 0;
    const difD = s.diferenciaDolares ?? 0;
    diferencias += difC;
    diferenciasDolares += difD;
    lineas.push(fila(t('turno.r.corte'), veredicto(t, difC)));
    if (s.primerConteoCordobas != null) lineas.push(fila(` ${t('turno.r.primerConteo')}`, formatMoney(s.primerConteoCordobas)));
    if (difD !== 0 || (s.conteoDolares ?? 0) > 0) lineas.push(fila(t('turno.r.corteDolares'), veredicto(t, difD, true)));
    if (s.primerConteoDolares != null) lineas.push(fila(` ${t('turno.r.primerConteo')}`, dolares(s.primerConteoDolares)));

    // The money: what stayed for change, and what went to the owner.
    lineas.push(fila(t('turno.r.queda'), formatMoney((s.queda ?? 0) as Centavos)));
    const saco = formatMoney((s.entregado ?? 0) as Centavos);
    lineas.push(fila(t('turno.r.saco'), saco));
    if ((s.entregadoDolares ?? 0) > 0) lineas.push(fila('', `+ ${dolares(s.entregadoDolares!)}`));
    if ((s.porRecibir ?? 0) > 0 || (s.porRecibirDolares ?? 0) > 0) lineas.push(...envolver(t('turno.r.porRecibir')));
  });

  lineas.push('='.repeat(ANCHO));
  lineas.push(...envolver(t('turno.r.totalDia')));
  lineas.push(fila(t('turno.r.vendido'), formatMoney(vendido as Centavos)));
  if (vendido > 0) lineas.push(fila(` ${t('turno.r.enEfectivo')}`, formatMoney((vendido - fiado - otros) as Centavos)));
  if (otros > 0) lineas.push(fila(` ${t('turno.r.otros')}`, formatMoney(otros as Centavos)));
  if (fiado > 0) lineas.push(fila(` ${t('turno.r.fiado')}`, formatMoney(fiado as Centavos)));
  lineas.push(fila(t('turno.r.diferencias'), diferencias === 0 ? t('turno.r.cuadro') : firmado(diferencias)));
  if (diferenciasDolares !== 0) lineas.push(fila('', firmado(diferenciasDolares, true)));
  return lineas.join('\n');
}
